import React, { useMemo, useState } from 'react';
import { AlertTriangle, ArrowUp, ArrowDown } from 'lucide-react';
import { UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import ReportWidget from './components/ReportWidget';
import { WorkOrderStatusBadge } from '../../components/Common';
import { formatCurrency as formatCurrencyBase } from '../../helpers/formatCurrency';
import { formatCreatedDate } from '../../constants/utils';

const COLORS = {
  labor: '#3b82f6',
  parts: '#f59e0b',
  contractor: '#10b981',
};

const EM_DASH = '—';

// Currency cells render without decimals; the shared helper handles locale + symbol.
const formatCurrency = (value, currency) =>
  formatCurrencyBase(value, currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

// Shared DD/MM/YYYY formatter — same helper TicketDetailsModal / Reactive.jsx use.
const formatDate = (iso) => formatCreatedDate(iso) || EM_DASH;

const SortIcon = ({ active, direction }) => {
  if (!active) return null;
  return direction === 'asc'
    ? <ArrowUp size={12} className="ms-1" />
    : <ArrowDown size={12} className="ms-1" />;
};

const cellStyle = (color) => ({
  backgroundColor: `${color}14`, // ~8% alpha
  color: '#111827',
  fontVariantNumeric: 'tabular-nums',
});

const numericCell = { fontVariantNumeric: 'tabular-nums' };

// Status enum order matches the WO module so the sort feels familiar.
const STATUS_ORDER = ['SUBMITTED', 'PENDING', 'IN_PROGRESS', 'ON_HOLD', 'RESOLVED'];
const statusRank = (s) => {
  const i = STATUS_ORDER.indexOf(s);
  return i === -1 ? STATUS_ORDER.length : i;
};

const WorkOrderCostByWoWidget = ({ data, loading, error, onRowClick }) => {
  const { t } = useTranslation();
  const rows = data?.rows || [];
  const summary = data?.summary;
  const currency = summary?.currency;

  const [sort, setSort] = useState({ column: 'total', direction: 'desc' });

  const sortedRows = useMemo(() => {
    if (!rows.length) return rows;
    const copy = [...rows];
    const { column, direction } = sort;
    copy.sort((a, b) => {
      const va = a?.[column];
      const vb = b?.[column];
      if (['labor', 'parts', 'contractor', 'total'].includes(column)) {
        const na = Number(va || 0);
        const nb = Number(vb || 0);
        return direction === 'asc' ? na - nb : nb - na;
      }
      if (column === 'firstOccurrence') {
        const da = va ? new Date(va).getTime() : 0;
        const db = vb ? new Date(vb).getTime() : 0;
        return direction === 'asc' ? da - db : db - da;
      }
      if (column === 'status') {
        const ra = statusRank(va);
        const rb = statusRank(vb);
        return direction === 'asc' ? ra - rb : rb - ra;
      }
      // title
      const sa = String(va ?? '').toLowerCase();
      const sb = String(vb ?? '').toLowerCase();
      return direction === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
    return copy;
  }, [rows, sort]);

  const setSortColumn = (column) => {
    setSort((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      const numeric = ['labor', 'parts', 'contractor', 'total', 'firstOccurrence'].includes(column);
      return { column, direction: numeric ? 'desc' : 'asc' };
    });
  };

  const isEmpty = !rows.length;

  const headerButton = (key, label) => (
    <button
      type="button"
      className="btn btn-link p-0 text-decoration-none fw-semibold text-dark d-inline-flex align-items-center"
      style={{ fontSize: '0.85rem' }}
      onClick={() => setSortColumn(key)}
    >
      {label}
      <SortIcon active={sort.column === key} direction={sort.direction} />
    </button>
  );

  return (
    <ReportWidget
      title={t('reporting.cost.table.title', 'Cost by work order')}
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={t('reporting.cost.table.empty', 'No cost data in this period.')}
    >
      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.85rem' }}>
          <thead>
            <tr className="text-muted" style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ minWidth: 220 }}>{headerButton('title', t('shared.title', 'Title'))}</th>
              <th style={{ minWidth: 130 }}>{headerButton('status', t('shared.status', 'Status'))}</th>
              <th style={{ minWidth: 130 }}>{headerButton('firstOccurrence', t('shared.first_occurrence', 'First Occurrence'))}</th>
              <th style={{ minWidth: 110, textAlign: 'right' }}>
                <span style={{ color: COLORS.labor }}>●</span>{' '}
                {headerButton('labor', t('reporting.cost.summary.labor', 'Labor'))}
              </th>
              <th style={{ minWidth: 110, textAlign: 'right' }}>
                <span style={{ color: COLORS.parts }}>●</span>{' '}
                {headerButton('parts', t('reporting.cost.summary.parts', 'Parts'))}
              </th>
              <th style={{ minWidth: 130, textAlign: 'right' }}>
                <span style={{ color: COLORS.contractor }}>●</span>{' '}
                {headerButton('contractor', t('reporting.cost.summary.contractor', 'Contractor'))}
              </th>
              <th style={{ minWidth: 110, textAlign: 'right' }}>
                {headerButton('total', t('reporting.cost.summary.total', 'Total'))}
              </th>
              <th style={{ width: 32 }} aria-label="" />
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => {
              const tooltipId = `cost-row-missing-${row.workOrderId}`;
              const titleClickable = !!onRowClick;
              return (
                <tr key={row.workOrderId}>
                  <td>
                    {titleClickable ? (
                      <button
                        type="button"
                        className="btn btn-link p-0 text-decoration-none fw-semibold text-primary text-start"
                        onClick={() => onRowClick(row)}
                      >
                        {row.title || row.workOrderNumber || EM_DASH}
                      </button>
                    ) : (
                      <span>{row.title || row.workOrderNumber || EM_DASH}</span>
                    )}
                  </td>
                  <td>
                    {row.status
                      ? <WorkOrderStatusBadge status={row.status} />
                      : EM_DASH}
                  </td>
                  <td style={numericCell}>{formatDate(row.firstOccurrence)}</td>
                  <td className="text-end" style={cellStyle(COLORS.labor)}>{formatCurrency(row.labor, currency)}</td>
                  <td className="text-end" style={cellStyle(COLORS.parts)}>{formatCurrency(row.parts, currency)}</td>
                  <td className="text-end" style={cellStyle(COLORS.contractor)}>{formatCurrency(row.contractor, currency)}</td>
                  <td className="text-end fw-bold" style={numericCell}>{formatCurrency(row.total, currency)}</td>
                  <td>
                    {row.hasMissingData && (
                      <>
                        <AlertTriangle id={tooltipId} size={14} className="text-warning" />
                        <UncontrolledTooltip placement="left" target={tooltipId}>
                          {t('reporting.cost.table.missing_data.tooltip',
                            'Some cost inputs are missing — total may be understated.')}
                        </UncontrolledTooltip>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {summary && (
            <tfoot>
              <tr className="fw-bold" style={{ borderTop: '2px solid #e5e7eb' }}>
                <td colSpan={3}>{t('reporting.cost.table.total_row', 'Total')}</td>
                <td className="text-end" style={numericCell}>{formatCurrency(summary.laborTotal, currency)}</td>
                <td className="text-end" style={numericCell}>{formatCurrency(summary.partsTotal, currency)}</td>
                <td className="text-end" style={numericCell}>{formatCurrency(summary.contractorTotal, currency)}</td>
                <td className="text-end" style={numericCell}>{formatCurrency(summary.totalSpend, currency)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </ReportWidget>
  );
};

export default WorkOrderCostByWoWidget;
